/* eslint-disable max-lines */
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ChartFilters } from 'src/app/models/chart-filters.model';
import { environment } from '../../../environments/environment';
import { IDegData } from '../../models/chart-deg.model';
import { IFdgData } from '../../models/chart-fdg.model';
import { IFogData } from '../../models/chart-fog.model';
import { ILpgData } from '../../models/chart-lpg.model';
import { IMwgData } from '../../models/chart-mwg.model';
import { ModelsComponents } from '../../models/models-components.model';
import { IVidData } from "../../models/chart-vid.model";
import { ChartVidVehiclePage } from "../../models/chart-vid-vehicle-page.model";
import { DatePipe } from '@angular/common';


@Injectable({
  providedIn: 'root',
})
export class ChartDataService {
  private baseUrl: string;

  constructor(public http: HttpClient, private datePipe: DatePipe
  ) {
    this.baseUrl = environment.apiBasePath + environment.apiVersionPath;
  }

  /**
   * Get models and components.
   */
  public getModelsAndComponents(chartFilters: ChartFilters): Observable<ModelsComponents> {
    // Returns empty observable if mandatory pars missing
    if ((!chartFilters.batchId || !chartFilters.lineId) && (!chartFilters.batchIds || !chartFilters.lineIds)) {
      return EMPTY;
    }

    let apiEndpoint;
    if ((!chartFilters.batchIds || !chartFilters.lineIds)) {
      apiEndpoint = `${this.baseUrl}/${environment.charts.modelsComponents
        .replace('{batchId}', chartFilters.batchId.toString())
        .replace('{lineNumber}', chartFilters.lineId)}`;
    } else {
      apiEndpoint = `${this.baseUrl}/${environment.charts.modelsComponents
        .replace('{batchId}', chartFilters.batchIds)
        .replace('{lineNumber}', chartFilters.lineIds)}`;
      apiEndpoint += '-multi-batch';
    }

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as ModelsComponents),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getModelsAndComponentsForSelectedModels(
    chartFilters: ChartFilters,
    models: string,
  ): Observable<ModelsComponents> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !chartFilters.lineId) {
      return EMPTY;
    }

    let apiEndpoint = `${this.baseUrl}/${environment.charts.modelsComponents
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineNumber}', chartFilters.lineId)}`;

    if (models) {
      apiEndpoint += '?models=' + models;
    }

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as ModelsComponents),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Get data for MWG (Mittelwertgrafik) charts
   */
  public getMwgData(chartFilters: ChartFilters, modelsComponents: ModelsComponents): Observable<IMwgData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !chartFilters.lineId) {
      return EMPTY;
    }

    const params = this.buildRequestParameters(chartFilters, modelsComponents);
    let updatedParams = params;
    if (chartFilters.startDate) {
      updatedParams = updatedParams.set('startDate', this.datePipe.transform(chartFilters.startDate, 'yyyy-MM-dd'));
    }

    if (chartFilters.endDate) {
      updatedParams = updatedParams.set('endDate', this.datePipe.transform(chartFilters.endDate, 'yyyy-MM-dd'));
    }

    const apiEndpoint = `${this.baseUrl}/${environment.charts.mwg
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineId}', chartFilters.lineId)}`;

    const requestOptions = {
      params: updatedParams,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IMwgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Get data for MWG (Mittelwertgrafik) charts
   */
  public getMwgDataForQC(chartFilters: ChartFilters, measurementId: number): Observable<IMwgData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !measurementId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.baseUrl}/${environment.charts.mwgQC
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineId}', 'CC')
      .replace('{measurementId}', measurementId.toString())}`;

    let params = new HttpParams();

    if (chartFilters.tolerance !== undefined) {
      params = params.set('tolerance', chartFilters.tolerance);
    }
    if (chartFilters.labSystem) {
      params = params.set('system', chartFilters.labSystem);
    }

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IMwgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Get data for MWG (Mittelwertgrafik) charts for QC measurement and a batch as reference (should be the cc type 3 measurement of the referenced batch)
   */
  public getMwgDataForQCForReference(chartFilters: ChartFilters, measurementId: number, refBatchId: number, refMeasurementId: number): Observable<IMwgData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !measurementId || (!refBatchId && !refMeasurementId)) {
      return EMPTY;
    }

    const apiEndpoint = `${this.baseUrl}/${environment.charts.mwgQCWithReference
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineId}', 'CC')
      .replace('{measurementId}', measurementId.toString())}`;

    let params = new HttpParams();

    if (chartFilters.tolerance !== undefined) {
      params = params.set('tolerance', chartFilters.tolerance);
    }
    if (chartFilters.labSystem) {
      params = params.set('system', chartFilters.labSystem);
    }
    // set ref batch id as reference if ref batch id is present
    if (refBatchId) {
      params = params.set('refBatchId', refBatchId);
    }
    // set ref measurement id as reference if refMeasurementId is present
    if (refMeasurementId) {
      params = params.set('refMeasurementId', refMeasurementId);
    }

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IMwgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getMwgDataForTheory(chartFilters: ChartFilters, theory: any, refBatchId: number, refMeasurementId: number): Observable<IMwgData> {
    // Returns empty observable if mandatory params missing
    if (!chartFilters.batchId || !theory) {
      return EMPTY;
    }

    const apiEndpoint = `${environment.apiBasePath}/${environment.charts.mwgForVectorTheory
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineId}', chartFilters.lineId ? chartFilters.lineId : 'CC')}`;

    let params = new HttpParams();

    if (chartFilters.tolerance !== undefined) {
      params = params.set('tolerance', chartFilters.tolerance);
    }
    if (chartFilters.labSystem) {
      params = params.set('system', chartFilters.labSystem);
    }
    // set ref batch id as reference if ref batch id is present
    if (refBatchId) {
      params = params.set('refBatchId', refBatchId);
    }
    // set ref measurement id as reference if refMeasurementId is present
    if (refMeasurementId) {
      params = params.set('refMeasurementId', refMeasurementId);
    }

    const requestOptions = {
      params,
    };

    return this.http.post(apiEndpoint, theory, requestOptions).pipe(
      map((response) => response as IMwgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getMwgDataOemLine(chartFilters: ChartFilters): Observable<IMwgData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters || !chartFilters.batchId || !chartFilters.lineId) {
      return EMPTY;
    }

    let params = new HttpParams();

    if (chartFilters.tolerance !== undefined) {
      params = params.set('tolerance', chartFilters.tolerance);
    }
    if (chartFilters.labSystem) {
      params = params.set('system', chartFilters.labSystem);
    }
    if (chartFilters.bumper === false) {
      params = params.set('bumperComponents', '-')
    }
    if (chartFilters.body === false) {
      params = params.set('bodyComponents', '-')
    }

    const apiEndpoint = `${this.baseUrl}/${environment.charts.mwg
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineId}', chartFilters.lineId)}`;

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IMwgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Get data for FDG (Farbdifferenzgrafik) charts
   */
  public getFdgData(chartFilters: ChartFilters, modelsComponents: ModelsComponents): Observable<IFdgData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !chartFilters.lineId) {
      return EMPTY;
    }

    let params = this.buildRequestParameters(chartFilters, modelsComponents);
    if (chartFilters.startDate) {
      params = params.set(
        'startDate',
        this.datePipe.transform(chartFilters.startDate, 'yyyy-MM-dd')
      );
    }

    if (chartFilters.endDate) {
      params = params.set(
        'endDate',
        this.datePipe.transform(chartFilters.endDate, 'yyyy-MM-dd')
      );
    }
    const apiEndpoint = `${this.baseUrl}/${environment.charts.fdg
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineId}', chartFilters.lineId)}`;

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IFdgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getFdgDataMultiBatch(chartFilters: ChartFilters, modelsComponents: ModelsComponents): Observable<IFdgData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchIds || !chartFilters.lineIds) {
      return EMPTY;
    }

    const params = this.buildRequestParameters(chartFilters, modelsComponents);

    const apiEndpoint = this.baseUrl + '/batch/' + chartFilters.batchIds + '/line/' + chartFilters.lineIds + '/data-diff-multi-batch';

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IFdgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Get data for FOG (FarbOrtgrafik) charts
   */
  public getFogData(chartFilters: ChartFilters, modelsComponents: ModelsComponents): Observable<IFogData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !chartFilters.lineId) {
      return EMPTY;
    }

    let params = this.buildRequestParameters(chartFilters, modelsComponents);
    if (chartFilters.startDate) {
      params = params.set(
        'startDate',
        this.datePipe.transform(chartFilters.startDate, 'yyyy-MM-dd')
      );
    }

    if (chartFilters.endDate) {
      params = params.set(
        'endDate',
        this.datePipe.transform(chartFilters.endDate, 'yyyy-MM-dd')
      );
    }

    const apiEndpoint = `${this.baseUrl}/${environment.charts.fog
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineNumber}', chartFilters.lineId)}`;

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IFogData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Get data for DEG (deltaE-Grafik) chart
   */
  public getDegData(chartFilters: ChartFilters, modelsComponents: ModelsComponents): Observable<IDegData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !chartFilters.lineId) {
      return EMPTY;
    }

    let params = this.buildRequestParameters(chartFilters, modelsComponents);
    if (chartFilters.startDate) {
      params = params.set(
        'startDate',
        this.datePipe.transform(chartFilters.startDate, 'yyyy-MM-dd')
      );
    }

    if (chartFilters.endDate) {
      params = params.set(
        'endDate',
        this.datePipe.transform(chartFilters.endDate, 'yyyy-MM-dd')
      );
    }
    const apiEndpoint = `${this.baseUrl}/${environment.charts.deg
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineNumber}', chartFilters.lineId)}`;

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IDegData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getDegDataMultiBatch(chartFilters: ChartFilters, modelsComponents: ModelsComponents): Observable<IDegData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchIds || !chartFilters.lineIds) {
      return EMPTY;
    }

    let params = this.buildRequestParameters(chartFilters, modelsComponents);
    if (chartFilters.startDate) {
      params = params.set(
        'startDate',
        this.datePipe.transform(chartFilters.startDate, 'yyyy-MM-dd')
      );
    }
    if (chartFilters.endDate) {
      params = params.set(
        'endDate',
        this.datePipe.transform(chartFilters.endDate, 'yyyy-MM-dd')
      );
    }

    const apiEndpoint = this.baseUrl + '/batch/' + chartFilters.batchIds + '/line/' + chartFilters.lineIds + '/data-diff-abstractde-multi-batch';

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IDegData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getLpgData(chartFilters: ChartFilters, modelsComponents: ModelsComponents): Observable<ILpgData> {
    // Returns empty observable if mandatory pars missing
    if (!chartFilters.batchId || !chartFilters.lineId) {
      return EMPTY;
    }

    let params = this.buildRequestParameters(chartFilters, modelsComponents);
    if (chartFilters.startDate) {
      params = params.set(
        'startDate',
        this.datePipe.transform(chartFilters.startDate, 'yyyy-MM-dd')
      );
    }

    if (chartFilters.endDate) {
      params = params.set(
        'endDate',
        this.datePipe.transform(chartFilters.endDate, 'yyyy-MM-dd')
      );
    }
    const apiEndpoint = `${this.baseUrl}/${environment.charts.lpg
      .replace('{batchId}', chartFilters.batchId.toString())
      .replace('{lineNumber}', chartFilters.lineId)}`;

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as ILpgData),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getVidData(chartFilters: ChartFilters, startDate: string, endDate: string): Observable<IVidData[]> {
    if (!chartFilters) {
      return EMPTY;
    }

    const apiEndpoint = String(environment.apiBasePath + environment.apiUserPath + '/' + environment.charts.vid);
    let params = new HttpParams();

    // params for batchIds and lineIds can be single or comma separated multiple
    if (chartFilters.batchIds && chartFilters.lineIds) {
      params = params.set('batchIds', chartFilters.batchIds);
      params = params.set('lines', chartFilters.lineIds);
    } else {
      params = params.set('batchIds', chartFilters.batchId);
      params = params.set('lines', chartFilters.lineId);
    }

    // selected models as params
    params = params.set('models', this.getChartParamModelComponentFilter(chartFilters.models));

    // set start and end date
    params = params.set('startDate', startDate);
    params = params.set('endDate', endDate);

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IVidData[]),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getVidVehicles(chartFilters: ChartFilters, startDate: string, endDate: string, currentPage: number, pageSize: number, minCarStatus: number, maxCarStatus: number): Observable<ChartVidVehiclePage> {
    if (!chartFilters) {
      return EMPTY;
    }

    const apiEndpoint = String(environment.apiBasePath + environment.apiUserPath + '/' + environment.charts.vidIds);
    let params = new HttpParams();

    // params for batchIds and lineIds can be single or comma separated multiple
    if (chartFilters.batchIds && chartFilters.lineIds) {
      params = params.set('batchIds', chartFilters.batchIds);
      params = params.set('lines', chartFilters.lineIds);
    } else {
      params = params.set('batchIds', chartFilters.batchId);
      params = params.set('lines', chartFilters.lineId);
    }

    // selected models as params
    params = params.set('models', this.getChartParamModelComponentFilter(chartFilters.models));

    // set start and end date
    params = params.set('startDate', startDate);
    params = params.set('endDate', endDate);

    if (minCarStatus >= 0 && maxCarStatus >= 0) {
      params = params.set('minCarStatus', minCarStatus);
      params = params.set('maxCarStatus', maxCarStatus);
    }

    // pagination params
    params = params.set('page', currentPage);
    params = params.set('size', pageSize);

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as ChartVidVehiclePage),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  private buildRequestParameters(chartFilters: ChartFilters, modelsComponents?: ModelsComponents): HttpParams {
    let params = new HttpParams();

    if (chartFilters.tolerance !== undefined) {
      params = params.set('tolerance', chartFilters.tolerance);
    }
    if (chartFilters.labSystem) {
      params = params.set('system', chartFilters.labSystem);
    }
    if (chartFilters.page !== undefined) {
      params = params.set('page', String(chartFilters.page));
    }

    params = params.set('models', this.getChartParamModelComponentFilter(chartFilters.models));

    if (modelsComponents && chartFilters.lineId === '*') {
      params = this.buildAverageRequestParameters(params, chartFilters, modelsComponents);
    } else {
      // If body components are not set all (*) body components should be used.
      params = params.set('bodyComponents', this.getChartParamModelComponentFilter(chartFilters.bodyComponents));

      // If bumper components are not set all (*) bumper components should be used.
      params = params.set('bumperComponents', this.getChartParamModelComponentFilter(chartFilters.bumperComponents));
    }

    return params;
  }

  private getChartParamModelComponentFilter(comps: string): string {
    if (comps === undefined || comps === null) {
      return '*';
    }
    if (comps === '') {
      return '-';
    }
    return comps;
  }

  private buildAverageRequestParameters(
    params: HttpParams,
    chartFilters: ChartFilters,
    modelsComponents: ModelsComponents,
  ): HttpParams {
    // Body and bumper components selected
    if (chartFilters.bodyComponents && chartFilters.bumperComponents) {
      if (chartFilters.bodyComponents !== '*' && chartFilters.bumperComponents !== '*') {
        params = params.set('bodyComponents', chartFilters.bodyComponents + ',' + chartFilters.bumperComponents);
      }
      // Only bodyComponents partially selected
      if (chartFilters.bodyComponents !== '*' && chartFilters.bumperComponents === '*') {
        modelsComponents.bumperComponents && modelsComponents.bumperComponents.length !== 0
          ? (params = params.set(
            'bodyComponents',
            chartFilters.bodyComponents + ',' + modelsComponents.bumperComponents.toString(),
          ))
          : (params = params.set('bodyComponents', chartFilters.bodyComponents));
      }
      // Only bumperComponents partially selected
      if (chartFilters.bodyComponents === '*' && chartFilters.bumperComponents !== '*') {
        modelsComponents.bodyComponents && modelsComponents.bodyComponents.length !== 0
          ? (params = params.set(
            'bodyComponents',
            modelsComponents.bodyComponents.toString() + ',' + chartFilters.bumperComponents,
          ))
          : (params = params.set('bodyComponents', chartFilters.bumperComponents));
      }
      // All components are selected
      if (chartFilters.bodyComponents === '*' && chartFilters.bumperComponents === '*') {
        params = params.set('bodyComponents', '*');
      }
    }
    // Only bodyComponents selected
    if (chartFilters.bodyComponents && !chartFilters.bumperComponents) {
      params = params.set('bodyComponents', chartFilters.bodyComponents);
    }
    // Only bumperComponents selected
    if (!chartFilters.bodyComponents && chartFilters.bumperComponents) {
      params = params.set('bodyComponents', chartFilters.bumperComponents);
    }
    return params;
  }
  // eslint-disable-next-line max-lines
}
